import React, { useState } from "react";
import FormInput from "../components/form/FormInput";
import FormTextarea from "../components/form/FormTextarea";
import FormDropdown from "../components/form/FormDropdown";
import HeaderSection from "../components/shared/HeaderSection";

const serviceOptions = [
  { value: "software-development", label: "Software Development" },
  { value: "hardware-solutions", label: "Hardware Solutions" },
  { value: "internet-services", label: "Internet Services" },
  { value: "mobile-app-development", label: "Mobile App Development" },
  { value: "web-app-development", label: "Web Application Development" },
  { value: "erp-consultation", label: "ERP Consultation" },
  { value: "security-solutions", label: "Security Solutions" },
  { value: "cctv-solutions", label: "CCTV Solutions" },
];

const Quote = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    details: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_QUOTATION_SERVICE}/quotation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccessMessage("Your quote request has been submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          service: "",
          details: "",
        });

        setTimeout(() => {
          setSuccessMessage("");
        }, 15000);
      } else {
        throw new Error("Failed to submit the quote request. Please try again.");
      }
    } catch (error) {
      setErrorMessage(error.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 to-white text-white">
      {/* Header Section */}
      <HeaderSection
        image="/images/bg-images/request-quote-bg.jpeg"
        header="Request a Quotation"
        subHeader="Get a Tailored Solution – Request Your Quote Today!"
      />

      {/* Main Content */}
      <div className="py-12 px-6">
        <div className="container mx-auto max-w-4xl bg-gradient-to-br from-blue-400 via-slate-850 to-slate-950 p-8 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold mb-6">Quotation Request Form</h2>
          <p className="text-gray-200 mb-8">
            Looking for a custom solution? Fill out our quotation request form
            with your project details, and our team will get back to you with a
            tailored proposal.
          </p>
          
          {/* Form */}
          <form className="space-y-6 mx-auto pb-2" onSubmit={handleSubmit}>
            <FormInput
              id="name"
              type="text"
              label="Name"
              placeholder="Your Name"
              required
              value={formData.name}
              onChange={handleChange}
            />
            <FormInput
              id="email"
              type="email"
              label="Email"
              placeholder="Your Email"
              required
              value={formData.email}
              onChange={handleChange}
            />
            <FormInput
              id="phone"
              type="tel"
              label="Phone"
              placeholder="Your Phone Number"
              required
              value={formData.phone}
              onChange={handleChange}
            />
            <FormInput
              id="company"
              type="text"
              label="Company Name"
              placeholder="Your Company Name"
              value={formData.company}
              onChange={handleChange}
            />
            <FormDropdown
              id="service"
              name="service"
              label="Service Required"
              options={serviceOptions}
              required
              value={formData.service}
              onChange={handleChange}
            />
            <FormTextarea
              id="details"
              label="Additional Details"
              placeholder="Provide any additional details about your project"
              value={formData.details}
              onChange={handleChange}
            />

            {/* Success/Error Messages */}
            {successMessage && <p className="text-green-500 mb-2">{successMessage}</p>}
            {errorMessage && <p className="text-red-500 mb-2">{errorMessage}</p>}

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </button>
          </form>

        </div>
      </div>
    </section>
  );
};

export default Quote;